import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

// Harmonious color palettes - modern, stylish gradients (darker tones)
const COLOR_PALETTES = [
  { start: '#4c5fd8', end: '#5a3a7a' }, // Dark purple gradient
  { start: '#c73dd4', end: '#c43d5a' }, // Dark pink gradient
  { start: '#3a8de8', end: '#00c4d6' }, // Dark blue gradient
  { start: '#2db85a', end: '#2bc4a8' }, // Dark green gradient
  { start: '#d85a7a', end: '#d4b030' }, // Dark pink-yellow gradient
  { start: '#24a5a6', end: '#28064d' }, // Dark cyan-purple gradient
  { start: '#7ab8b0', end: '#c4a0a8' }, // Dark soft pastel gradient
  { start: '#cc6a7a', end: '#c49fa8' }, // Dark soft pink gradient
  { start: '#ccb08a', end: '#c47a6a' }, // Dark warm orange gradient
  { start: '#cc6a5a', end: '#b83a6a' }, // Dark red-pink gradient
  { start: '#4c5fd8', end: '#5a3a7a' }, // Dark purple-blue gradient
  { start: '#c4a030', end: '#c4705a' }, // Dark orange gradient
  { start: '#5ac880', end: '#6ab0c4' }, // Dark mint-blue gradient
  { start: '#6a8ac4', end: '#8aa0c4' }, // Dark light blue gradient
  { start: '#ccb08a', end: '#c47a6a' }, // Dark peach gradient
  { start: '#cc6a30', end: '#cc4a5a' }, // Dark coral gradient
];

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-address-avatar',
  template: `
    <div 
      class="address-avatar" 
      [style.background]="backgroundGradient">
      <div class="avatar-inner">
        {{ hexChars }}
      </div>
    </div>
  `,
  styles: [`
    .address-avatar {
      width: 3.2rem;
      height: 3.2rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      position: relative;
      transition: transform 0.2s ease, border-color 0.2s ease;
      
      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: -1px;
        right: -1px;
        bottom: -1px;
        border-radius: 50%;
        border: 1px solid rgba(var(--dark-text), 0.5);
        pointer-events: none;
      }
    }
    
    .avatar-inner {
      position: relative;
      z-index: 1;
      font-size: 1.5rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #ffffff;
      line-height: 1;
    }
  `]
})
export class AddressAvatarComponent implements OnChanges {
  @Input() address!: string;
  @Input() size: number = 3.2; // Size in rem

  backgroundGradient: string = 'linear-gradient(135deg, #4c5fd8 0%, #5a3a7a 100%)';
  textColor: string = '#ffffff';
  hexChars: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['address'] && this.address) {
      this.generateAvatar();
    }
  }

  private generateAvatar(): void {
    if (!this.address) {
      return;
    }

    // Get first 2 hex characters (after 0x if present)
    const cleanAddress = this.address.startsWith('0x') 
      ? this.address.slice(2, 4) 
      : this.address.slice(0, 2);
    
    // Store hex characters separately for styling
    this.hexChars = cleanAddress;

    // Generate color palette from address hash
    const hash = this.simpleHash(this.address);
    const paletteIndex = hash % COLOR_PALETTES.length;
    const palette = COLOR_PALETTES[paletteIndex];
    
    // Create gradient with slight variation based on hash
    const angle = 135 + (hash % 60) - 30; // 105 to 165 degrees
    this.backgroundGradient = `linear-gradient(${angle}deg, ${palette.start} 0%, ${palette.end} 100%)`;
    
    // Always use white text for better contrast with gradients
    this.textColor = '#ffffff';
  }

  private simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }
}

